import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Upload } from "lucide-react";
import { ApiError, type WispImportFormat, type WispImportPreviewDto, type WispImportResultDto } from "@finora/api-client";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";

const inputCls = "h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";
const labelCls = "mb-1 block text-xs font-medium text-slate-600";

function errMsg(err: unknown, fallback: string) {
  if (err instanceof ApiError) {
    const m = err.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return fallback;
}

const SOURCES: { key: WispImportFormat; title: string; accept: string; how: React.ReactNode }[] = [
  {
    key: "FREERADIUS_SQL",
    title: "FreeRADIUS baza (i daloRADIUS)",
    accept: ".sql,.txt,.dump",
    how: (
      <>
        Na starom RADIUS serveru izvezi bazu u fajl i ubaci ga ovde. MySQL/MariaDB:{" "}
        <code className="rounded bg-slate-100 px-1">mysqldump radius &gt; radius.sql</code>, PostgreSQL:{" "}
        <code className="rounded bg-slate-100 px-1">pg_dump radius &gt; radius.sql</code>. Uzimaju se korisnici, lozinke, grupe (paketi), brzine,
        fiksne IP adrese i podaci kupaca iz daloRADIUS-a.
      </>
    ),
  },
  {
    key: "USERMANAGER",
    title: "MikroTik User Manager",
    accept: ".rsc,.txt",
    how: (
      <>
        U Terminalu rutera (RouterOS 7): <code className="rounded bg-slate-100 px-1">/user-manager export show-sensitive file=um</code>, za RouterOS 6:{" "}
        <code className="rounded bg-slate-100 px-1">/tool user-manager export file=um</code>. Fajl <b>um.rsc</b> preuzmi iz Files i ubaci ovde.
      </>
    ),
  },
  {
    key: "PPP_SECRETS",
    title: "MikroTik PPP (korisnici upisani u ruteru)",
    accept: ".rsc,.txt",
    how: (
      <>
        U Terminalu rutera: <code className="rounded bg-slate-100 px-1">/ppp export show-sensitive file=ppp</code> (na RouterOS 6 bez{" "}
        <i>show-sensitive</i>). Fajl <b>ppp.rsc</b> preuzmi iz Files i ubaci ovde. Brzina se uzima iz PPP profila, ime iz komentara.
      </>
    ),
  },
  {
    key: "TABLE",
    title: "Excel ili CSV tabela",
    accept: ".xlsx,.xls,.csv",
    how: (
      <>
        Za bilo koji drugi sistem: popuni tabelu (ime, telefon, adresa, korisničko ime, lozinka, paket, brzine u Mbps…) i ubaci je ovde.
        Prazna tabela sa primerom je na dugmetu ispod.
      </>
    ),
  },
];

const STATUS_LABEL = { ACTIVE: "aktivan", SUSPENDED: "suspendovan", DISABLED: "isključen" } as const;

export function ImportPanel({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ["wisp-plans"], queryFn: () => api.listWispPlans() });
  const [format, setFormat] = useState<WispImportFormat>("FREERADIUS_SQL");
  const [file, setFile] = useState<File | null>(null);
  const [defaultPlanId, setDefaultPlanId] = useState("");
  const [missingPassword, setMissingPassword] = useState<"GENERATE" | "SKIP">("GENERATE");
  const [preview, setPreview] = useState<WispImportPreviewDto | null>(null);
  const [result, setResult] = useState<WispImportResultDto | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [onlySkipped, setOnlySkipped] = useState(false);

  const opts = { defaultPlanId: defaultPlanId || null, missingPassword };
  const reset = () => {
    setPreview(null);
    setErr(null);
  };

  const doPreview = useMutation({
    mutationFn: () => api.previewWispImport(file!, format, opts),
    onSuccess: (p) => {
      setErr(null);
      setPreview(p);
    },
    onError: (e) => {
      setPreview(null);
      setErr(errMsg(e, "Fajl ne može da se pročita."));
    },
  });
  const doCommit = useMutation({
    mutationFn: () => api.commitWispImport(file!, format, opts),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ["wisp-subscribers"] });
      qc.invalidateQueries({ queryKey: ["wisp-plans"] });
      qc.invalidateQueries({ queryKey: ["wisp-overview"] });
    },
    onError: (e) => setErr(errMsg(e, "Uvoz nije uspeo.")),
  });
  const template = useMutation({
    mutationFn: () => api.downloadWispImportTemplate(),
    onSuccess: (blob) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "uvoz-pretplatnika.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    },
  });

  const downloadPasswords = (r: WispImportResultDto) => {
    const csv = "Korisničko ime;Nova lozinka\n" + r.generatedPasswords.map((g) => `${g.username};${g.password}`).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
    a.download = "nove-lozinke.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (result) {
    return (
      <div className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
        <p className="font-semibold">
          Uvezeno {result.created} pretplatnika{result.skipped ? `, preskočeno ${result.skipped}` : ""}.
        </p>
        {result.newPlans.length > 0 && <p>Napravljeni paketi: {result.newPlans.join(", ")}. Proveri im naziv i poveži artikal za fakturu (kartica „Paketi").</p>}
        {result.generatedPasswords.length > 0 && (
          <div className="rounded-lg bg-amber-50 p-3 text-amber-900">
            <p className="font-medium">
              {result.generatedPasswords.length} korisnika je dobilo NOVU lozinku (stara nije mogla da se pročita). Te nove lozinke treba upisati u
              njihove uređaje. Spisak se prikazuje samo sada - preuzmi ga.
            </p>
            <Button variant="secondary" onClick={() => downloadPasswords(result)}>
              <Download className="mr-1 h-4 w-4" /> Preuzmi spisak novih lozinki
            </Button>
          </div>
        )}
        <Button onClick={onClose}>Zatvori</Button>
      </div>
    );
  }

  const src = SOURCES.find((x) => x.key === format)!;
  const rows = preview ? preview.rows.filter((r) => !onlySkipped || r.action === "SKIP") : [];

  return (
    <div className="space-y-4 rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">Uvoz pretplatnika iz starog sistema</h2>
        <button type="button" className="text-sm text-slate-500 underline" onClick={onClose}>
          zatvori
        </button>
      </div>

      <div className="grid gap-2 md:grid-cols-4">
        {SOURCES.map((x) => (
          <button
            key={x.key}
            type="button"
            onClick={() => {
              setFormat(x.key);
              setFile(null);
              reset();
            }}
            className={`rounded-lg border p-3 text-left text-sm ${format === x.key ? "border-brand bg-slate-50 font-medium" : "border-border"}`}
          >
            {x.title}
          </button>
        ))}
      </div>

      <p className="text-sm text-slate-600">{src.how}</p>
      {format === "TABLE" && (
        <Button variant="secondary" onClick={() => template.mutate()} disabled={template.isPending}>
          <Download className="mr-1 h-4 w-4" /> Preuzmi praznu tabelu
        </Button>
      )}

      <div className="grid gap-3 md:grid-cols-3">
        <div>
          <label className={labelCls}>Fajl</label>
          <input
            type="file"
            accept={src.accept}
            className="block w-full text-sm"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              reset();
            }}
          />
        </div>
        <div>
          <label className={labelCls}>Paket za korisnike bez poznate brzine</label>
          <select
            className={inputCls}
            value={defaultPlanId}
            onChange={(e) => {
              setDefaultPlanId(e.target.value);
              reset();
            }}
          >
            <option value="">— ne uvozi ih —</option>
            {plans.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Ako lozinka ne može da se pročita</label>
          <select
            className={inputCls}
            value={missingPassword}
            onChange={(e) => {
              setMissingPassword(e.target.value as "GENERATE" | "SKIP");
              reset();
            }}
          >
            <option value="GENERATE">napravi novu lozinku</option>
            <option value="SKIP">ne uvozi tog korisnika</option>
          </select>
        </div>
      </div>

      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}

      <div className="flex gap-2">
        <Button onClick={() => doPreview.mutate()} disabled={!file || doPreview.isPending}>
          <Upload className="mr-1 h-4 w-4" /> {doPreview.isPending ? "Čitam…" : "Pregled"}
        </Button>
      </div>

      {preview && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
            {[
              ["U fajlu", preview.total],
              ["Biće uvezeno", preview.toImport],
              ["Preskočeno", preview.skipped],
              ["Nova lozinka", preview.newPasswords],
              ["Isključenih", preview.disabled],
            ].map(([l, v]) => (
              <div key={String(l)} className="rounded-lg bg-slate-50 p-2">
                <div className="text-xs text-slate-500">{l}</div>
                <div className="text-lg font-semibold">{v}</div>
              </div>
            ))}
          </div>
          {preview.newPlans.length > 0 && (
            <p className="text-sm text-slate-600">
              Napraviće se paketi:{" "}
              {preview.newPlans.map((p) => `${p.name} (${p.downloadMbps}/${p.uploadMbps} Mbps)`).join(", ")}
            </p>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={onlySkipped} onChange={(e) => setOnlySkipped(e.target.checked)} /> prikaži samo preskočene
          </label>
          <div className="max-h-96 overflow-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 text-left text-slate-500">
                <tr>
                  <th className="px-2 py-1">Korisnik</th>
                  <th className="px-2 py-1">Kupac</th>
                  <th className="px-2 py-1">Paket</th>
                  <th className="px-2 py-1">Stanje</th>
                  <th className="px-2 py-1">Napomena</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.source}-${i}`} className={`border-t border-border ${r.action === "SKIP" ? "bg-red-50" : ""}`}>
                    <td className="px-2 py-1 font-mono">{r.username || "—"}</td>
                    <td className="px-2 py-1">{[r.name, r.phone].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="px-2 py-1">{r.plan ?? "—"}</td>
                    <td className="px-2 py-1">{STATUS_LABEL[r.status]}</td>
                    <td className="px-2 py-1">
                      {r.action === "SKIP" ? <span className="text-red-700">preskače se: {r.reason}</span> : null}
                      {r.newPassword && <span className="text-amber-700">nova lozinka </span>}
                      {r.warnings.join("; ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.rows.length < preview.total && <p className="text-xs text-slate-500">Prikazano prvih {preview.rows.length} redova.</p>}
          <Button onClick={() => doCommit.mutate()} disabled={doCommit.isPending || preview.toImport === 0}>
            {doCommit.isPending ? "Uvozim…" : `Uvezi ${preview.toImport} pretplatnika`}
          </Button>
        </div>
      )}
    </div>
  );
}
